<template>
  <v-card
    :title="t('components.AccessCodeTablePage.create_voucher')"
    :loading="store.networkBusy">
    <v-card-text>
      <v-form>
        <h4 data-pw="accountTypeHeader">{{ $t('components.CreateVoucherCard.account_type') }}</h4>
        <choose-voucher-account-type v-model="voucherAccountType" data-pw="accountTypeList"/>
        <div class="my-4">
          <h4>{{ $t("components.CreateVoucherCard.quantity") }}</h4>
          <v-slider
            v-model="quantity"
            data-pw="quantitySlider"
            class="align-center"
            density="compact"
            :max="999"
            :min="1"
            :step="1"
            hide-details
          >
            <template v-slot:prepend>
              <v-text-field
                v-model="quantity"
                hide-details
                single-line
                data-pw="quantitySliderInput"
                variant="outlined"
                density="compact"
                type="number"
                style="width: 90px"
              ></v-text-field>
            </template>
          </v-slider>
        </div>
        <div class="mt-4">
          <h4>
            {{ $t('components.CreateVoucherCard.tenanted') }}
          </h4>
          <v-switch :label="$t('components.CreateVoucherCard.belongs_to_tenant')" color="green" v-model="tenanted" />
        </div>
        <v-sheet v-if="tenanted" color="grey-lighten-3" rounded class="pa-4 mb-4">
          <h3>{{ $t('components.CreateVoucherCard.tenant_settings') }}</h3>
          <span
            class="text-caption">{{ $t('components.CreateVoucherCard.tenant_settings_text_caption') }}</span>
          <tenant-search-autocomplete
            v-model="selectedTenant"
            entity-type="tenant"
          />

          <tenant-search-autocomplete
            v-model="selectedInstitution"
            :disabled="!selectedTenant"
            entity-type="institution"
            :tenant-ids="selectedTenant ? [selectedTenant] : []"
          />

          <tenant-search-autocomplete
            v-model="selectedCohort"
            :disabled="!selectedInstitution"
            entity-type="cohort"
            :institution-ids="selectedInstitution ? [selectedInstitution] : []"
          />
        </v-sheet>
        <h4>
          {{ $t('components.CreateVoucherCard.research') }}
        </h4>
        <v-switch :label="$t('components.CreateVoucherCard.used_for_research')" color="green"  v-model="research"/>
        <v-sheet v-if="research" color="grey-lighten-3" rounded class="pa-4">
          <h3>
            {{
              $t('components.CreateVoucherCard.research_assessments')
            }}
          </h3>
          <span class="text-caption">
            {{
              $t('components.CreateVoucherCard.research_settings_text_caption')
            }}
          </span>
          <v-checkbox v-for="(assessment, index) in assessmentTypeOptions"
                      :key="index"
                      :label="assessment.title"
                      :value="assessment.value"
                      density="compact"
                      hide-details
                      @click="() => toggleResearchAssessment(assessment.value)"/>
        </v-sheet>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-btn-secondary
        @click="$emit('close')"
      >
        {{ $t('buttons.cancel') }}
      </v-btn-secondary>
      <v-btn-primary
        :disabled="!canConfirm || store.networkBusy"
        data-pw="confirmButton"
        @click="showConfirmation = true"
      >
        {{ $t('buttons.confirm') }}
      </v-btn-primary>
    </v-card-actions>
  </v-card>

  <v-dialog v-model="showConfirmation" max-width="500px">
    <v-card>
      <v-card-title>{{ $t('components.CreateVoucherCard.confirmation') }}</v-card-title>
      <v-card-text>
        <p>{{ confirmationMessage }}</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn-secondary @click="showConfirmation = false">
          {{ $t('buttons.cancel') }}
        </v-btn-secondary>
        <v-btn-primary
          :disabled="store.networkBusy"
          data-pw="finalConfirmButton"
          @click="createAccessCode"
        >
          {{ $t('buttons.confirm') }}
        </v-btn-primary>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-snackbar v-model="store.snackbarVisible">
    {{ store.snackbarMessage }}
  </v-snackbar>
</template>

<script setup lang="ts">
import yapi from "@/lib/yapi";
import {
  AccessCode,
  CoreAssessmentType,
  isYapiError,
  YapiError,
  AccountType
} from "@YenzaCT/sdk";
import {computed, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useGlobalStore} from "@/store";
import ChooseVoucherAccountType from "@/components/Voucher/ChooseVoucherAccountType.vue";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";
const store = useGlobalStore();
const {t} = useI18n();
const tenanted = ref(false);
const showConfirmation = ref(false);
const selectedTenant = ref<string | null>();
const selectedInstitution = ref<string | null>();
const selectedCohort = ref<string | null>();
const voucherAccountType = ref<AccountType>(AccountType.Starter);
const quantity = ref(1);
const research = ref(false);

const emit = defineEmits<{
  (e: "confirm"): void;
  (e: "close"): void;
}>();

// Watchers to reset error when selections change
watch([selectedTenant], () => {
  selectedInstitution.value = null;
  selectedCohort.value = null;
});

watch([selectedInstitution], () => {
  selectedCohort.value = null;
});

const assessmentTypeOptions = ref(Object.values(CoreAssessmentType)
  .map((slug) => ({
    title: t(`components.CreateVoucherCard.assessments.${slug}`),
    value: slug
  })));

const selectedAssessmentsDefault: Record<string, unknown> = {};
Object.values(CoreAssessmentType)
  .forEach((slug) => {
    selectedAssessmentsDefault[slug] = false;
  });
const researchSelected = ref(selectedAssessmentsDefault);

function toggleResearchAssessment(assessment: string | CoreAssessmentType) {
  researchSelected.value[assessment] = !researchSelected.value[assessment];
}

const canConfirm = computed(() => {
  const tenantSettingsValid = tenanted.value ? !!(tenanted.value && selectedCohort.value && selectedCohort.value) : true;

  const atLeastOneResearchSelected = Object.values(researchSelected.value)
    .some((v) => v);
  const researchSettingsValid = research.value ? atLeastOneResearchSelected : true;

  return tenantSettingsValid && researchSettingsValid;
});
const confirmationMessage = computed(() => {
  const baseMessage = t("components.CreateVoucherCard.confirmationMessage", {
    quantity: quantity.value,
    accountType: t(`accountTypes.${voucherAccountType.value}`),
    tenant: tenanted.value ? selectedTenant.value : "",
    institution: tenanted.value ? selectedInstitution.value : "",
    cohort: tenanted.value ? selectedCohort.value : ""
  });
  return tenanted.value
    ? baseMessage
    : baseMessage.replace(/\s*for\s*,\s*,\s*/, "");
});

const createAccessCode = async () => {
  store.networkBusy = true;
  try {
    const accessCode = {
      accountType: voucherAccountType.value,
      researchAssessments: Object.keys(researchSelected.value)
        .filter((key) => researchSelected.value[key]),
    } as AccessCode;

    if (tenanted.value && selectedCohort.value && selectedCohort.value) {
      accessCode.cohort = selectedCohort.value;
      accessCode.institution = selectedInstitution.value;
      accessCode.tenant = selectedTenant.value;
    }

    await yapi.admin.accessCode.create(accessCode, quantity.value);
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
      store.handleNetworkLoadingAndSnackbar(t("components.CreateVoucherCard.snackbarErrorMessage"));
    } else {
      throw e;
    }
  } finally {
    store.handleNetworkLoadingAndSnackbar(`${t("components.CreateVoucherCard.snackbarCompletedMessage") + quantity.value}`);
    emit("close");
  }
};
</script>
